import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LoadingOverlay from '../components/overlays/LoadingOverlay';
import ErrorModal from '../components/modals/ErrorModal';
import ProfileFormSheet from '../components/modals/ProfileFormSheet';
import CornerNotice from '../components/notifications/CornerNotice';
import { useAuth } from '../contexts/AuthContext';
import { usePageConfig } from '../contexts/PageConfigContext';
import { isSystemOrganizationContext } from '../utils/groupsPage';
import {
  appendAsOfDateQuery,
  canViewAllOrganizationsWorkSamples,
  computeLpTimelineFlexWeights,
  formatDevAsOfLabel,
  formatLpDateRange,
  formatLpShortDate,
  isWorkSamplesDevToolsEnabled,
  lpTodayProgress,
  readDevAsOfDate,
  writeDevAsOfDate,
  getWorkSamplesPageCapabilities,
  WS_TIMELINE_LEGEND,
  describeWorkSamplesAccess,
  formatIssuesSummary,
  formatLpProgress,
  buildNextPortfolioSubmissionIds,
} from '../utils/workSamplesPage';
import { buildUserProfileUrl } from '../utils/userProfileUrl';
import LpMiniCalendar from '../components/workSamples/LpMiniCalendar';
import StudentPortfolioExplorer from '../components/workSamples/StudentPortfolioExplorer';
import PortfolioStatusBadges from '../components/workSamples/PortfolioStatusBadges';
import PortfolioEvidenceHelpSheet from '../components/workSamples/PortfolioEvidenceHelp';
import { buildLearningPeriodCalendarSection } from '../utils/scheduleCalendar';
import './WorkSamplesManager.css';

const SettingsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const FolderIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
  </svg>
);

const PersonIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

function LearningPeriodTimeline({ periods, selectedLpId, referenceDate, onSelectLp }) {
  const flexWeights = useMemo(() => computeLpTimelineFlexWeights(periods), [periods]);

  if (!periods.length) return null;

  return (
    <div className="ws-lp-timeline">
      <div className="ws-lp-timeline-track" role="tablist" aria-label="Learning periods">
        {periods.map((lp, index) => {
          const isSelected = String(selectedLpId) === String(lp.id);
          const isNow = lp.timing_status === 'current';
          const status = lp.timing_status || 'unknown';
          const todayPct = lpTodayProgress(lp, referenceDate);
          const dateLabel = [formatLpShortDate(lp.start_date), formatLpShortDate(lp.end_date)]
            .filter(Boolean)
            .join(' – ');

          return (
            <button
              key={lp.id}
              type="button"
              role="tab"
              aria-selected={isSelected}
              className={[
                'ws-lp-timeline-segment',
                `ws-lp-timeline-segment--${status}`,
                isNow ? 'ws-lp-timeline-segment--now' : '',
                isSelected ? 'is-selected' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{ flex: flexWeights[index] || 1 }}
              onClick={() => onSelectLp(lp.id)}
              title={formatLpDateRange(lp.start_date, lp.end_date)}
            >
              <div className="ws-lp-timeline-bar-wrap">
                <div className="ws-lp-timeline-bar" />
                {isNow && todayPct != null && (
                  <span
                    className="ws-lp-timeline-today"
                    style={{ left: `${todayPct * 100}%` }}
                    title="Today"
                    aria-hidden
                  />
                )}
                {isNow && (
                  <span className="ws-lp-timeline-now-label">Now</span>
                )}
              </div>
              <span className="ws-lp-timeline-title">{lp.title}</span>
              {dateLabel && <span className="ws-lp-timeline-dates">{dateLabel}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function profileUrlForStudent(student, returnPath) {
  return buildUserProfileUrl({
    userId: student.student_user_id,
    userGroupsPeriodsId: student.student_ugp_id,
    from: returnPath,
  });
}

const WS_LEGEND_ACTIONS = [
  {
    key: 'profile',
    Icon: PersonIcon,
    label: 'Profile',
    description: 'Open the student profile for this enrollment.',
  },
  {
    key: 'portfolio',
    Icon: FolderIcon,
    label: 'Portfolio',
    description:
      'Open the portfolio explorer — all learning periods, form assignments, evidence, and search.',
  },
  {
    key: 'settings',
    Icon: SettingsIcon,
    label: 'Table columns',
    description: 'Show or hide Vendor and Grade columns.',
  },
];

function trackKey(trackPeriodId) {
  return trackPeriodId ? String(trackPeriodId) : '__none__';
}

function currentLpSortIndex(student, trackOptions) {
  const opt = trackOptions.find(
    (t) => trackKey(t.track_period_id) === trackKey(student.track_period_id)
  );
  if (!opt || !student.current_learning_period_id) return 9999;
  const idx = opt.learning_periods.findIndex(
    (lp) => String(lp.id) === String(student.current_learning_period_id)
  );
  return idx >= 0 ? idx : 9999;
}

function getStudentLpCell(student, lpId) {
  if (!lpId || !student?.learning_periods?.length) return null;
  return (
    student.learning_periods.find((c) => String(c.learning_period_id) === String(lpId)) || null
  );
}


function getDisplayLpCell(student, filterViewLpId) {
  const lpId = filterViewLpId || student.current_learning_period_id;
  return getStudentLpCell(student, lpId);
}

function canOpenPortfolioExplorer(student) {
  return Boolean(student?.has_track && student?.current_learning_period_id);
}

function portfolioExplorerBlockedMessage(student) {
  if (!student?.has_track) {
    return 'This student has no track assigned. Assign a track before opening the portfolio explorer.';
  }
  if (!student?.current_learning_period_id) {
    return 'This student has no learning period on their track. Add learning periods before opening the portfolio explorer.';
  }
  return null;
}

function PortfolioStatusBadge({ lifecycleStatus, openFlagCount = 0 }) {
  return (
    <PortfolioStatusBadges lifecycleStatus={lifecycleStatus} openFlagCount={openFlagCount} />
  );
}

const ROSTER_VIEWS = Object.freeze({
  ALL: 'all',
  MINE: 'mine',
  REVIEW: 'review',
});

function studentMatchesTrackFilter(student, filterTrackKey) {
  return trackKey(student.track_period_id) === filterTrackKey;
}

function WorkSamplesManager() {
  const { fetchWithAuth, currentRole } = useAuth();
  const location = useLocation();
  const pageConfig = usePageConfig();
  const profileReturnPath = `${location.pathname}${location.search}`;
  const caps = useMemo(
    () => getWorkSamplesPageCapabilities(pageConfig?.configuration),
    [pageConfig?.configuration]
  );

  const [dashboard, setDashboard] = useState(null);
  const [forms, setForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorModal, setErrorModal] = useState(null);
  const [cornerNotice, setCornerNotice] = useState(null);
  const [fatalError, setFatalError] = useState(null);
  const [filterTrackKey, setFilterTrackKey] = useState('');
  const [filterViewLpId, setFilterViewLpId] = useState('');
  const [showColVendor, setShowColVendor] = useState(false);
  const [showColGrade, setShowColGrade] = useState(false);
  const [colSettingsOpen, setColSettingsOpen] = useState(false);
  const colSettingsRef = useRef(null);
  const [assigning, setAssigning] = useState(false);
  const [curatingSubmissionId, setCuratingSubmissionId] = useState(null);
  const [portfolioLifecycleBusy, setPortfolioLifecycleBusy] = useState(false);
  const [rosterView, setRosterView] = useState(ROSTER_VIEWS.ALL);
  const [portfolioModal, setPortfolioModal] = useState(null);
  const [showLegendModal, setShowLegendModal] = useState(false);
  const [showPortfolioHelp, setShowPortfolioHelp] = useState(false);
  const accessDescription = useMemo(
    () =>
      describeWorkSamplesAccess({
        viewScope: caps.viewScope,
        orgName: dashboard?.workflow?.org_name || null,
        viewerVendors: dashboard?.summary?.viewer_vendors || [],
      }),
    [
      caps.viewScope,
      dashboard?.workflow?.org_name,
      dashboard?.summary?.viewer_vendors,
    ]
  );
  const devToolsEnabled = isWorkSamplesDevToolsEnabled();
  const initialDevAsOf = readDevAsOfDate();
  const [devAsOfDateApplied, setDevAsOfDateApplied] = useState(initialDevAsOf);
  const [devAsOfDateDraft, setDevAsOfDateDraft] = useState(initialDevAsOf);
  const [orgOptions, setOrgOptions] = useState([]);
  const [selectedOrgGpId, setSelectedOrgGpId] = useState('');
  const [schedulerPeriods, setSchedulerPeriods] = useState([]);
  const [schedulerPeriodsLoading, setSchedulerPeriodsLoading] = useState(false);
  const [schedulerPeriodsError, setSchedulerPeriodsError] = useState('');

  const isSystemContext = isSystemOrganizationContext(currentRole);
  const canPickOrg = canViewAllOrganizationsWorkSamples(caps);
  const showOrgSetupHint = !canPickOrg && !isSystemContext;

  const organizationGpId = currentRole?.organization_groups_periods_id
    ? String(currentRole.organization_groups_periods_id)
    : '';

  const activeOrgGpId = canPickOrg ? selectedOrgGpId : organizationGpId;

  const orgGpParam = activeOrgGpId
    ? `org_groups_periods_id=${encodeURIComponent(activeOrgGpId)}`
    : '';

  const trackOptions = useMemo(
    () => dashboard?.track_options || [],
    [dashboard?.track_options]
  );

  const selectedTrack = useMemo(
    () => trackOptions.find((t) => trackKey(t.track_period_id) === filterTrackKey) || null,
    [trackOptions, filterTrackKey]
  );

  const lpOptions = useMemo(
    () => selectedTrack?.learning_periods || [],
    [selectedTrack]
  );

  const filterViewLp = useMemo(
    () => lpOptions.find((lp) => String(lp.id) === String(filterViewLpId)) || null,
    [lpOptions, filterViewLpId]
  );

  const filteredStudents = useMemo(() => {
    let list = dashboard?.students ? [...dashboard.students] : [];
    if (filterTrackKey) {
      list = list.filter((s) => studentMatchesTrackFilter(s, filterTrackKey));
    }
    list.sort((a, b) => {
      const trackCmp = (a.track_label || 'No track').localeCompare(
        b.track_label || 'No track',
        undefined,
        { sensitivity: 'base' }
      );
      if (trackCmp !== 0) return trackCmp;
      return currentLpSortIndex(a, trackOptions) - currentLpSortIndex(b, trackOptions);
    });
    return list;
  }, [dashboard?.students, trackOptions, filterTrackKey]);

  const isLpFocusMode = Boolean(filterViewLpId);

  const lpCalendarSection = useMemo(() => {
    if (!filterViewLpId || !schedulerPeriods.length) return null;
    return buildLearningPeriodCalendarSection(filterViewLpId, schedulerPeriods);
  }, [filterViewLpId, schedulerPeriods]);

  useEffect(() => {
    const orgGroupId = dashboard?.workflow?.organizationGroupId;
    if (!filterViewLpId || !orgGroupId) {
      setSchedulerPeriods([]);
      setSchedulerPeriodsError('');
      setSchedulerPeriodsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setSchedulerPeriodsLoading(true);
      setSchedulerPeriodsError('');
      try {
        const params = new URLSearchParams({
          organization_group_id: String(orgGroupId)
        });
        const res = await fetchWithAuth(`/api/schedule-builder/periods?${params.toString()}`);
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || data.message || 'Failed to load schedule');
        }
        if (!cancelled) {
          setSchedulerPeriods(data.periods || []);
        }
      } catch (err) {
        if (!cancelled) {
          setSchedulerPeriods([]);
          setSchedulerPeriodsError(err.message || 'Could not load learning period calendar.');
        }
      } finally {
        if (!cancelled) setSchedulerPeriodsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [filterViewLpId, dashboard?.workflow?.organizationGroupId, fetchWithAuth]);

  const includesOrgRoster = dashboard?.summary?.includes_org_roster === true;
  const torLinkedCount = dashboard?.summary?.tor_linked_count ?? 0;
  const showRosterTabs = includesOrgRoster && (torLinkedCount > 0 || caps.canReviewPortfolios);

  const rosterFilteredStudents = useMemo(() => {
    let list = filteredStudents;
    if (!showRosterTabs) return list;
    if (rosterView === ROSTER_VIEWS.MINE) {
      list = list.filter((s) => s.is_tor_linked);
    } else if (rosterView === ROSTER_VIEWS.REVIEW) {
      list = list.filter((s) => (s.awaiting_review_count || 0) > 0 || (s.open_flag_count || 0) > 0);
    }
    return list;
  }, [filteredStudents, rosterView, showRosterTabs]);

  const rosterSummary = useMemo(() => {
    const list = rosterFilteredStudents;
    const needing = list.filter((s) => s.needs_attention).length;
    const flagged = list.filter((s) => (s.open_flag_count || 0) > 0).length;
    const currentLpIncomplete = list.filter((s) => {
      const st = s.current_portfolio_status;
      const forms = s.current_form_count || 0;
      const evidence = s.current_evidence_count || 0;
      return !st || st === 'draft' || (forms > 0 && evidence === 0);
    }).length;
    return {
      total: list.length,
      needing,
      flagged,
      currentLpIncomplete,
    };
  }, [rosterFilteredStudents]);

  useEffect(() => {
    if (!dashboard) return;
    if (includesOrgRoster && caps.canReviewPortfolios) {
      setRosterView(ROSTER_VIEWS.ALL);
    } else if (torLinkedCount > 0) {
      setRosterView(ROSTER_VIEWS.MINE);
    } else {
      setRosterView(ROSTER_VIEWS.ALL);
    }
  }, [activeOrgGpId, includesOrgRoster, caps.canReviewPortfolios, torLinkedCount, dashboard]);

  const handleTrackBadgeClick = (key) => {
    setFilterTrackKey((prev) => (prev === key ? '' : key));
    setFilterViewLpId('');
  };

  const handleLpBadgeClick = (lpId) => {
    setFilterViewLpId((prev) => (String(prev) === String(lpId) ? '' : String(lpId)));
  };

  const devAsOfDraftValid = /^\d{4}-\d{2}-\d{2}$/.test(devAsOfDateDraft);
  const devAsOfDraftDirty = devAsOfDateDraft !== devAsOfDateApplied;

  const applyDevAsOfDate = () => {
    if (!devAsOfDraftValid) return;
    writeDevAsOfDate(devAsOfDateDraft);
    setDevAsOfDateApplied(devAsOfDateDraft);
  };

  const clearDevAsOfDate = () => {
    writeDevAsOfDate('');
    setDevAsOfDateApplied('');
    setDevAsOfDateDraft('');
  };

  const showError = (title, message, details = '') => {
    setErrorModal({ title, message, details: details || undefined });
  };

  const showCornerNotice = (payload) => {
    setCornerNotice({
      isOpen: true,
      title: payload.title || 'Saved',
      text: payload.text || payload.message || '',
      position: 'bottom-left',
      autoClose: true,
      viewTime: 4000,
    });
  };

  const showPortfolioFatalError = (title, message, details = '') => {
    setFatalError({ title, message, details: details || undefined });
  };

  const closePortfolioModal = () => {
    if (portfolioSheetBusy) return;
    setPortfolioModal(null);
    setShowPortfolioHelp(false);
  };

  const loadOrgOptions = useCallback(async () => {
    if (!canPickOrg) {
      setOrgOptions([]);
      return;
    }
    try {
      const res = await fetchWithAuth('/api/work-samples/orgs-with-workflow');
      const data = await res.json();
      if (data.success) {
        setOrgOptions(data.data || []);
      }
    } catch {
      setOrgOptions([]);
    }
  }, [fetchWithAuth, canPickOrg]);

  const loadDashboard = useCallback(async ({ silent = false } = {}) => {
    if (canPickOrg && !selectedOrgGpId) {
      setDashboard({ requires_org_selection: true, workflow: { enabled: false } });
      if (!silent) setIsLoading(false);
      return;
    }

    if (!silent) setIsLoading(true);
    try {
      let url = orgGpParam
        ? `/api/work-samples/tor-dashboard?${orgGpParam}`
        : '/api/work-samples/tor-dashboard';
      url = appendAsOfDateQuery(url, devAsOfDateApplied);
      const res = await fetchWithAuth(url);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load dashboard');
      setDashboard(data.data);
    } catch (err) {
      if (silent) {
        showPortfolioFatalError(
          'Refresh failed',
          'Could not refresh the roster after your last change.',
          err.message
        );
      } else {
        showError('Load failed', 'Could not load Work Samples dashboard.', err.message);
        setDashboard(null);
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [fetchWithAuth, orgGpParam, canPickOrg, selectedOrgGpId, devAsOfDateApplied]);

  const loadForms = useCallback(async () => {
    if (!caps.canAssignForms) return;
    try {
      const res = await fetchWithAuth('/api/forms');
      const data = await res.json();
      if (data.success) setForms(data.data || []);
    } catch {
      /* optional */
    }
  }, [fetchWithAuth, caps.canAssignForms]);

  useEffect(() => {
    loadOrgOptions();
  }, [loadOrgOptions]);

  useEffect(() => {
    loadDashboard();
    loadForms();
  }, [loadDashboard, loadForms]);

  useEffect(() => {
    setFilterTrackKey('');
    setFilterViewLpId('');
  }, [activeOrgGpId]);

  useEffect(() => {
    if (!colSettingsOpen) return undefined;
    const onDocClick = (e) => {
      if (colSettingsRef.current && !colSettingsRef.current.contains(e.target)) {
        setColSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [colSettingsOpen]);

  const fetchPortfolioExplorer = useCallback(
    async (student) => {
      const orgGroupId = dashboard?.workflow?.organizationGroupId;
      if (!orgGroupId) {
        throw new Error('Organization context is required to load portfolios.');
      }

      const params = new URLSearchParams({
        student_ugp_id: student.student_ugp_id,
        organization_group_id: String(orgGroupId),
      });
      let url = `/api/work-samples/portfolio-explorer?${params.toString()}`;
      url = appendAsOfDateQuery(url, devAsOfDateApplied);
      const res = await fetchWithAuth(url);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load portfolio explorer');
      return data.data;
    },
    [dashboard?.workflow?.organizationGroupId, devAsOfDateApplied, fetchWithAuth]
  );

  const refreshPortfolioExplorer = useCallback(
    async (student) => {
      setPortfolioModal((m) => (m ? { ...m, refreshing: true } : m));
      try {
        const explorer = await fetchPortfolioExplorer(student);
        setPortfolioModal((m) =>
          m ? { ...m, explorer, loading: false, refreshing: false } : m
        );
        return true;
      } catch (err) {
        setPortfolioModal((m) => (m ? { ...m, refreshing: false } : m));
        showPortfolioFatalError(
          'Portfolio update failed',
          'Your last change may not have saved correctly.',
          err.message
        );
        return false;
      }
    },
    [fetchPortfolioExplorer]
  );

  const openPortfolioExplorer = async (student) => {
    if (fatalError) return;

    const blockedMessage = portfolioExplorerBlockedMessage(student);
    if (blockedMessage) {
      showError('Unavailable', blockedMessage);
      return;
    }

    try {
      setPortfolioModal({
        student,
        explorer: null,
        loading: true,
        refreshing: false,
        highlightLpId: filterViewLpId || null,
        highlightTrackKey: filterTrackKey || null,
      });

      const explorer = await fetchPortfolioExplorer(student);
      setPortfolioModal((m) => ({
        ...m,
        explorer,
        loading: false,
        refreshing: false,
      }));
    } catch (err) {
      setPortfolioModal((m) =>
        m ? { ...m, explorer: null, loading: false, refreshing: false } : m
      );
      if (err.message?.includes('Organization context')) {
        showError('Unavailable', err.message);
      } else {
        showError('Load failed', 'Could not load portfolio explorer for this student.', err.message);
      }
    }
  };

  const handleAssignForm = async (student, learningPeriodId, formId) => {
    if (!student || !formId || !learningPeriodId || fatalError) return;
    setAssigning(true);
    try {
      const res = await fetchWithAuth('/api/work-samples/assign-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_ugp_id: student.student_ugp_id,
          form_id: formId,
          learning_period_id: learningPeriodId,
          org_groups_periods_id: activeOrgGpId || null,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Assign failed');

      if (portfolioModal?.student) {
        const refreshed = await refreshPortfolioExplorer(portfolioModal.student);
        if (!refreshed) return;
      }
      await loadDashboard({ silent: true });

      showCornerNotice({
        title: 'Assigned',
        text: 'Form assigned to student and added to the portfolio.',
      });
    } catch (err) {
      showPortfolioFatalError('Assign failed', err.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleTogglePortfolioSubmission = async ({
    portfolioId,
    form,
    submissionId,
    action,
  }) => {
    if (!portfolioId || !submissionId || !form || fatalError) return;

    const portfolioLp = portfolioModal?.explorer?.learning_periods?.find(
      (lp) => String(lp.portfolio?.id) === String(portfolioId)
    );
    const currentIds = portfolioLp?.portfolio?.form_submission_ids || [];

    setCuratingSubmissionId(submissionId);
    try {
      const nextIds = buildNextPortfolioSubmissionIds(currentIds, form, submissionId, action);
      const res = await fetchWithAuth(`/api/work-samples/portfolios/${portfolioId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_submission_ids: nextIds }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Could not update portfolio evidence');

      if (portfolioModal?.student) {
        const refreshed = await refreshPortfolioExplorer(portfolioModal.student);
        if (!refreshed) return;
      }
      await loadDashboard({ silent: true });

      showCornerNotice({
        title: action === 'remove' ? 'Removed from portfolio' : 'Added to portfolio',
        text:
          action === 'remove'
            ? 'Submission removed from portfolio evidence.'
            : 'Submission included in portfolio evidence.',
      });
    } catch (err) {
      showPortfolioFatalError('Update failed', err.message);
    } finally {
      setCuratingSubmissionId(null);
    }
  };

  const handlePortfolioAction = async (action, portfolioId) => {
    if (!portfolioId || fatalError) return;
    const routeMap = {
      submit: 'submit',
      'start-review': 'start-review',
      pass: 'pass',
    };
    const route = routeMap[action];
    if (!route) return;

    setPortfolioLifecycleBusy(true);
    try {
      const res = await fetchWithAuth(`/api/work-samples/portfolios/${portfolioId}/${route}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Portfolio action failed');

      if (portfolioModal?.student) {
        const refreshed = await refreshPortfolioExplorer(portfolioModal.student);
        if (!refreshed) return;
      }
      await loadDashboard({ silent: true });

      const noticeMap = {
        submit: { title: 'Submitted', text: 'Portfolio submitted for VDM review.' },
        'start-review': { title: 'In review', text: 'Portfolio marked in review.' },
        pass: { title: 'Passed', text: 'Portfolio marked passed.' },
      };
      showCornerNotice(noticeMap[action] || { title: 'Saved', text: 'Portfolio updated.' });
    } catch (err) {
      showPortfolioFatalError('Portfolio action failed', err.message);
    } finally {
      setPortfolioLifecycleBusy(false);
    }
  };

  const canCuratePortfolioEvidence = caps.canCurate;
  const portfolioMutationBusy =
    assigning || !!curatingSubmissionId || !!portfolioModal?.refreshing || portfolioLifecycleBusy;
  const portfolioInitialLoading = !!(portfolioModal?.loading && !portfolioModal?.explorer);
  const portfolioSheetBusy = portfolioMutationBusy || portfolioInitialLoading || !!fatalError;
  const portfolioOverlayVisible = !!portfolioModal && (portfolioMutationBusy || portfolioInitialLoading);
  const portfolioOverlayMessage =
    portfolioModal?.loading && !portfolioModal?.explorer
      ? 'Loading portfolio explorer…'
      : 'Saving…';

  if (isLoading && !dashboard) {
    return <LoadingOverlay isVisible message="Loading Work Samples…" />;
  }

  if (dashboard?.requires_org_selection) {
    return (
      <div className="ws-page">
        <div className="ws-inner">
          <header className="ws-header">
            <h1 className="ws-title">Work Samples</h1>
            <p className="ws-subtitle">
              Cross-organization view — select a school organization to inspect its work sample
              workflow and TOR roster.
            </p>
          </header>
          <div className="ws-empty-card">
            <label className="ws-filter-label" htmlFor="ws-org-select">
              Organization
            </label>
            <select
              id="ws-org-select"
              className="ws-select ws-org-select"
              value={selectedOrgGpId}
              onChange={(e) => setSelectedOrgGpId(e.target.value)}
            >
              <option value="">Select organization…</option>
              {orgOptions.map((org) => (
                <option key={org.org_gp_id} value={org.org_gp_id}>
                  {org.org_name}
                  {org.gp_title ? ` · ${org.gp_title}` : ''}
                </option>
              ))}
            </select>
            {orgOptions.length === 0 && (
              <p className="ws-empty-hint" style={{ marginTop: 12 }}>
                No organizations have the Work Samples workflow enabled yet.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!dashboard?.workflow?.enabled) {
    const orgLabel =
      dashboard?.workflow?.org_name ||
      orgOptions.find((o) => String(o.org_gp_id) === String(selectedOrgGpId))?.org_name;

    return (
      <div className="ws-page">
        <div className="ws-inner">
          <header className="ws-header">
            <h1 className="ws-title">Work Samples</h1>
            <p className="ws-subtitle">
              {canPickOrg
                ? orgLabel
                  ? `Work sample workflow is not enabled for ${orgLabel}.`
                  : 'Select an organization with the workflow enabled.'
                : isSystemContext
                  ? 'Switch to a school organization role, or grant All organizations view scope to pick an org here.'
                  : 'The work sample compliance workflow is not enabled for this organization, or no org context is selected.'}
            </p>
          </header>
          {canPickOrg && (
            <div className="ws-empty-card" style={{ marginBottom: 16 }}>
              <label className="ws-filter-label" htmlFor="ws-org-select-disabled">
                Organization
              </label>
              <select
                id="ws-org-select-disabled"
                className="ws-select ws-org-select"
                value={selectedOrgGpId}
                onChange={(e) => setSelectedOrgGpId(e.target.value)}
              >
                <option value="">Select organization…</option>
                {orgOptions.map((org) => (
                  <option key={org.org_gp_id} value={org.org_gp_id}>
                    {org.org_name}
                    {org.gp_title ? ` · ${org.gp_title}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="ws-empty-card">
            {showOrgSetupHint ? (
              <p>
                Assign the Work Samples app to this organization&apos;s group-period in Apps
                Manager.
              </p>
            ) : (
              <p>
                {orgLabel
                  ? `${orgLabel} does not have an active Work Samples app assignment on its organization group-period.`
                  : 'Choose a different organization or switch your role context.'}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingOverlay isVisible message="Loading Work Samples…" />;
  }

  const { students } = dashboard;

  return (
    <div className="ws-page">
      <div className="ws-inner">
        <header className="ws-header">
          <div>
            <h1 className="ws-title">Work Samples</h1>
            <p className="ws-subtitle">
              <span className="ws-access-label">{accessDescription.label}</span>
              {accessDescription.before}
              {accessDescription.emphasis ? (
                <strong>{accessDescription.emphasis}</strong>
              ) : null}
              {accessDescription.after}
            </p>
          </div>
          {canPickOrg && orgOptions.length > 0 && (
            <div className="ws-header-controls">
              <div className="ws-filter-field">
                <label htmlFor="ws-org-select-header" className="ws-filter-label">
                  Organization
                </label>
                <select
                  id="ws-org-select-header"
                  className="ws-select ws-org-select"
                  value={selectedOrgGpId}
                  onChange={(e) => setSelectedOrgGpId(e.target.value)}
                >
                  {orgOptions.map((org) => (
                    <option key={org.org_gp_id} value={org.org_gp_id}>
                      {org.org_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </header>

        {devToolsEnabled && (
          <div className="ws-dev-asof" role="group" aria-label="Development date override">
            <label className="ws-dev-asof-label" htmlFor="ws-dev-asof-date">
              Pretend today
            </label>
            <input
              id="ws-dev-asof-date"
              type="date"
              className="ws-dev-asof-input"
              value={devAsOfDateDraft}
              onChange={(e) => setDevAsOfDateDraft(e.target.value)}
            />
            <button
              type="button"
              className="ws-dev-asof-set"
              disabled={!devAsOfDraftValid || !devAsOfDraftDirty}
              onClick={applyDevAsOfDate}
            >
              Set
            </button>
            {devAsOfDateApplied ? (
              <>
                <span className="ws-dev-asof-badge">
                  Simulating {formatDevAsOfLabel(devAsOfDateApplied)}
                </span>
                <button
                  type="button"
                  className="ws-dev-asof-reset"
                  onClick={clearDevAsOfDate}
                >
                  Use real today
                </button>
              </>
            ) : null}
          </div>
        )}

        {showRosterTabs && (
          <div className="ws-filter-bar">
            <div className="ws-filter-group">
              <span className="ws-filter-group-label">Roster</span>
              <button
                type="button"
                className={`ws-filter-badge${rosterView === ROSTER_VIEWS.ALL ? ' is-active' : ''}`}
                aria-pressed={rosterView === ROSTER_VIEWS.ALL}
                onClick={() => setRosterView(ROSTER_VIEWS.ALL)}
              >
                All students
              </button>
              {torLinkedCount > 0 ? (
                <button
                  type="button"
                  className={`ws-filter-badge${rosterView === ROSTER_VIEWS.MINE ? ' is-active' : ''}`}
                  aria-pressed={rosterView === ROSTER_VIEWS.MINE}
                  onClick={() => setRosterView(ROSTER_VIEWS.MINE)}
                >
                  My students
                </button>
              ) : null}
              {caps.canReviewPortfolios ? (
                <button
                  type="button"
                  className={`ws-filter-badge ws-filter-badge--attention${
                    rosterView === ROSTER_VIEWS.REVIEW ? ' is-active' : ''
                  }`}
                  aria-pressed={rosterView === ROSTER_VIEWS.REVIEW}
                  onClick={() => setRosterView(ROSTER_VIEWS.REVIEW)}
                >
                  Review queue
                </button>
              ) : null}
            </div>
          </div>
        )}

        {trackOptions.length > 0 && (
          <div className="ws-filter-bar">
            <div className="ws-filter-group">
              <span className="ws-filter-group-label">Track</span>
              {trackOptions.map((t) => {
                const key = trackKey(t.track_period_id);
                const isActive = filterTrackKey === key;
                return (
                  <button
                    key={key}
                    type="button"
                    className={`ws-filter-badge${isActive ? ' is-active' : ''}`}
                    aria-pressed={isActive}
                    onClick={() => handleTrackBadgeClick(key)}
                  >
                    {t.track_label}
                  </button>
                );
              })}
            </div>
            {filterTrackKey && lpOptions.length > 0 && (
              <div className="ws-lp-timeline-section">
                <div className="ws-lp-timeline-head">
                  <span className="ws-filter-group-label">Learning period</span>
                  <button
                    type="button"
                    className="ws-info-btn"
                    aria-label="Legend — timeline and icons"
                    onClick={() => setShowLegendModal(true)}
                  >
                    <InfoIcon />
                  </button>
                </div>
                <LearningPeriodTimeline
                  periods={lpOptions}
                  selectedLpId={filterViewLpId}
                  referenceDate={devAsOfDateApplied}
                  onSelectLp={handleLpBadgeClick}
                />
              </div>
            )}
          </div>
        )}

        <section className="ws-roster-section" aria-label="Student roster">
          {isLpFocusMode && filterViewLp && (
            <div className="ws-lp-focus-banner" role="status" aria-live="polite">
              <div className="ws-lp-focus-banner-top">
                <span className="ws-lp-focus-banner-icon" aria-hidden>
                  <FilterIcon />
                </span>
                <div className="ws-lp-focus-banner-body">
                  <p className="ws-lp-focus-banner-title">
                    Table filtered to <strong>{filterViewLp.title}</strong>
                  </p>
                  <p className="ws-lp-focus-banner-desc">
                    Forms, evidence, progress, and flags below are scoped to this learning period
                    {selectedTrack?.track_label ? (
                      <> on <strong>{selectedTrack.track_label}</strong></>
                    ) : null}
                    .
                    {(filterViewLp.start_date || filterViewLp.end_date) && (
                      <span className="ws-lp-focus-banner-dates">
                        {' '}
                        {formatLpDateRange(filterViewLp.start_date, filterViewLp.end_date)}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  className="ws-lp-focus-banner-clear"
                  onClick={() => setFilterViewLpId('')}
                >
                  Clear filter
                </button>
              </div>
              <div className="ws-lp-focus-banner-calendar">
                <p className="ws-lp-focus-banner-cal-label">Included dates (student schedule)</p>
                <LpMiniCalendar
                  section={lpCalendarSection}
                  referenceDateIso={devAsOfDateApplied || undefined}
                  loading={schedulerPeriodsLoading}
                  error={schedulerPeriodsError}
                />
              </div>
            </div>
          )}
          <div className="ws-roster-head">
            <div className="ws-roster-head-text">
              <h2 className="ws-section-title">Students</h2>
              <p className="ws-section-sub">
                {isLpFocusMode
                  ? 'Use row actions to assign portfolio forms or open the full portfolio.'
                  : 'Overview — current learning period portfolio status and issues across all past/current periods.'}
              </p>
            </div>
            <div className="ws-toolbar" ref={colSettingsRef}>
              <button
                type="button"
                className="ws-toolbar-btn"
                aria-label="Legend — colors and icons"
                onClick={() => setShowLegendModal(true)}
              >
                <InfoIcon />
              </button>
              <button
                type="button"
                className="ws-toolbar-btn"
                aria-label="Table column settings"
                aria-expanded={colSettingsOpen}
                onClick={() => setColSettingsOpen((v) => !v)}
              >
                <SettingsIcon />
              </button>
              {colSettingsOpen && (
                <div className="ws-col-settings" role="menu">
                  <label className="ws-col-settings-label">
                    <input
                      type="checkbox"
                      checked={showColVendor}
                      onChange={(e) => setShowColVendor(e.target.checked)}
                    />
                    Vendor
                  </label>
                  <label className="ws-col-settings-label">
                    <input
                      type="checkbox"
                      checked={showColGrade}
                      onChange={(e) => setShowColGrade(e.target.checked)}
                    />
                    Grade
                  </label>
                </div>
              )}
            </div>
          </div>

          {filteredStudents.length > 0 && (
            <div className="ws-summary-strip" role="status" aria-live="polite">
              <span><strong>{rosterSummary.total}</strong> student{rosterSummary.total === 1 ? '' : 's'}</span>
              {rosterSummary.needing > 0 && (
                <span className="ws-summary-strip--warn">
                  <strong>{rosterSummary.needing}</strong> need attention
                </span>
              )}
              {rosterSummary.flagged > 0 && (
                <span className="ws-summary-strip--flag">
                  <strong>{rosterSummary.flagged}</strong> with open flags
                </span>
              )}
              {!isLpFocusMode && rosterSummary.currentLpIncomplete > 0 && (
                <span className="ws-summary-strip--muted">
                  <strong>{rosterSummary.currentLpIncomplete}</strong> current LP incomplete
                </span>
              )}
            </div>
          )}

          {students.length === 0 ? (
            <div className="ws-empty-card">
              <p>
                {includesOrgRoster
                  ? 'No students found for this organization.'
                  : 'No students linked as Teacher of Record yet.'}
              </p>
              {!includesOrgRoster ? (
                <p className="ws-empty-hint">
                  Add a <strong>Teacher of Record</strong> person link on each student profile.
                </p>
              ) : null}
            </div>
          ) : rosterFilteredStudents.length === 0 ? (
            <div className="ws-empty-card">
              <p>No students match the current roster filter.</p>
              <p className="ws-empty-hint">Try a different roster tab, track, or learning period filter.</p>
            </div>
          ) : (
            <div className="ws-table-wrap">
              <table className="ws-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    {showColVendor && <th>Vendor</th>}
                    {!isLpFocusMode && <th>Track</th>}
                    {!isLpFocusMode && <th>Current LP</th>}
                    {showColGrade && !isLpFocusMode && <th>Grade</th>}
                    <th>{isLpFocusMode ? 'Portfolio' : 'Portfolio (current)'}</th>
                    {isLpFocusMode ? (
                      <>
                        <th>Forms</th>
                        <th>Evidence</th>
                        <th>Progress</th>
                        <th>Flags</th>
                      </>
                    ) : (
                      <>
                        <th>Forms / evidence</th>
                        <th>Issues</th>
                      </>
                    )}
                    <th className="ws-col-actions" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {rosterFilteredStudents.map((student) => {
                    const lpCell = getDisplayLpCell(student, filterViewLpId);
                    const needsAttention = isLpFocusMode
                      ? lpCell?.needs_attention === true
                      : student.needs_attention === true;
                    const portfolioStatus = isLpFocusMode
                      ? lpCell?.portfolio_status
                      : student.current_portfolio_status;
                    const formCount = isLpFocusMode
                      ? lpCell?.form_count ?? 0
                      : student.current_form_count ?? 0;
                    const evidenceCount = isLpFocusMode
                      ? lpCell?.evidence_count ?? 0
                      : student.current_evidence_count ?? 0;
                    const openFlags = isLpFocusMode
                      ? lpCell?.open_flag_count ?? 0
                      : student.open_flag_count ?? 0;
                    const canOpenExplorer = canOpenPortfolioExplorer(student);
                    const explorerBlockedMessage = portfolioExplorerBlockedMessage(student);

                    return (
                      <tr
                        key={student.student_ugp_id}
                        className={[
                          canOpenExplorer ? 'ws-row--clickable' : '',
                          needsAttention ? 'ws-row--warn' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={(e) => {
                          if (!canOpenExplorer) return;
                          if (e.target.closest('a, button')) return;
                          openPortfolioExplorer(student);
                        }}
                      >
                        <td>
                          <Link
                            to={profileUrlForStudent(student, profileReturnPath)}
                            className="ws-student-link"
                          >
                            {student.name}
                          </Link>
                          {student.is_tor_linked ? (
                            <span className="ws-student-meta">My student</span>
                          ) : student.group_name ? (
                            <span className="ws-student-meta">{student.group_name}</span>
                          ) : null}
                        </td>
                        {showColVendor && <td>{student.vendor_label || '—'}</td>}
                        {!isLpFocusMode && <td>{student.track_label || '—'}</td>}
                        {!isLpFocusMode && (
                          <td>
                            {student.current_learning_period_title ? (
                              <span
                                className={
                                  student.current_learning_period_timing_status === 'current'
                                    ? 'ws-lp-cell-title ws-lp-cell-title--now'
                                    : 'ws-lp-cell-title'
                                }
                              >
                                {student.current_learning_period_title}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        )}
                        {showColGrade && !isLpFocusMode && <td>{student.grade_level || '—'}</td>}
                        <td>
                          <PortfolioStatusBadge
                            lifecycleStatus={portfolioStatus}
                            openFlagCount={openFlags}
                          />
                        </td>
                        {isLpFocusMode ? (
                          <>
                            <td>
                              <span className="ws-count">{formCount}</span>
                            </td>
                            <td>
                              <span
                                className={
                                  needsAttention && evidenceCount === 0 && formCount > 0
                                    ? 'ws-count ws-count--warn'
                                    : 'ws-count'
                                }
                              >
                                {evidenceCount}
                              </span>
                            </td>
                            <td>
                              <span className="ws-progress-label">{formatLpProgress(lpCell)}</span>
                            </td>
                            <td>
                              {openFlags > 0 ? (
                                <span className="ws-count ws-count--flag">{openFlags}</span>
                              ) : (
                                <span className="ws-muted-dash">—</span>
                              )}
                            </td>
                          </>
                        ) : (
                          <>
                            <td>
                              <span className="ws-forms-evidence">
                                <span className="ws-count">{formCount}</span>
                                <span className="ws-forms-evidence-sep">forms</span>
                                <span
                                  className={
                                    needsAttention && evidenceCount === 0 && formCount > 0
                                      ? 'ws-count ws-count--warn'
                                      : 'ws-count'
                                  }
                                >
                                  {evidenceCount}
                                </span>
                                <span className="ws-forms-evidence-sep">evidence</span>
                              </span>
                            </td>
                            <td>
                              <span
                                className={[
                                  'ws-issues-summary',
                                  needsAttention ? 'ws-issues-summary--warn' : '',
                                  !student.has_track && !needsAttention ? 'ws-issues-summary--muted' : '',
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                              >
                                {formatIssuesSummary(student)}
                                {student.lp_issue_titles?.length > 0 && (
                                  <span className="ws-issues-detail">
                                    {student.lp_issue_titles.slice(0, 3).join(', ')}
                                    {student.lp_issue_titles.length > 3 ? '…' : ''}
                                  </span>
                                )}
                              </span>
                            </td>
                          </>
                        )}
                        <td className="ws-actions-cell">
                          <button
                            type="button"
                            className="ws-icon-btn ws-icon-btn--portfolio"
                            title={
                              canOpenExplorer
                                ? 'Portfolio explorer'
                                : explorerBlockedMessage || 'Portfolio explorer unavailable'
                            }
                            aria-label={
                              canOpenExplorer
                                ? `Portfolio explorer for ${student.name}`
                                : `Portfolio explorer unavailable for ${student.name}`
                            }
                            disabled={!canOpenExplorer}
                            onClick={() => openPortfolioExplorer(student)}
                          >
                            <FolderIcon />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <ProfileFormSheet
        open={showLegendModal}
        onClose={() => setShowLegendModal(false)}
        title="Legend"
        titleId="ws-legend-title"
        subtext="Timeline, counts, and table icons on this page."
        footer={
          <button
            type="button"
            className="profile-sheet-btn profile-sheet-btn--secondary"
            onClick={() => setShowLegendModal(false)}
          >
            Close
          </button>
        }
      >
        <p className="ws-legend-section-label">Learning period timeline</p>
        <ul className="ws-legend-list">
          {WS_TIMELINE_LEGEND.map((item) => (
            <li key={item.key} className="ws-legend-item">
              <span className={item.className} aria-hidden />
              <div className="ws-legend-item-text">
                <strong>{item.label}</strong>
                <span>{item.description}</span>
              </div>
            </li>
          ))}
        </ul>
        <p className="ws-legend-section-label">Portfolio & issues</p>
        <ul className="ws-legend-list">
          <li className="ws-legend-item">
            <span className="ws-legend-swatch ws-portfolio-status ws-portfolio-status--flagged" aria-hidden />
            <div className="ws-legend-item-text">
              <strong>Portfolio status</strong>
              <span>Lifecycle: Draft → Submitted → In review → Passed. Open flags show as a separate attention badge.</span>
            </div>
          </li>
          <li className="ws-legend-item">
            <span className="ws-legend-swatch ws-legend-swatch--warn" aria-hidden />
            <div className="ws-legend-item-text">
              <strong>Needs attention</strong>
              <span>Amber row when a past/current LP portfolio is incomplete or has open VDM flags.</span>
            </div>
          </li>
        </ul>
        <p className="ws-legend-section-label">Row actions</p>
        <ul className="ws-legend-list">
          {WS_LEGEND_ACTIONS.map(({ key, Icon, label, description }) => (
            <li key={key} className="ws-legend-item">
              <span className="ws-legend-icon-preview" aria-hidden>
                <Icon />
              </span>
              <div className="ws-legend-item-text">
                <strong>{label}</strong>
                <span>{description}</span>
              </div>
            </li>
          ))}
        </ul>
      </ProfileFormSheet>

      <ProfileFormSheet
        open={!!portfolioModal}
        onClose={closePortfolioModal}
        closeDisabled={portfolioSheetBusy}
        title={
          portfolioModal ? (
            <span className="ws-sheet-title-with-info">
              Portfolio — {portfolioModal.student.name}
              <button
                type="button"
                className="ws-info-btn ws-info-btn--title"
                aria-label="How portfolio forms and evidence work"
                onClick={() => setShowPortfolioHelp(true)}
              >
                <InfoIcon />
              </button>
            </span>
          ) : (
            'Portfolio'
          )
        }
        titleId="ws-portfolio-title"
        panelClassName="ws-portfolio-sheet-panel"
        overlayClassName="profile-sheet-overlay--raised"
        subtext={
          filterViewLp
            ? `Focused on ${filterViewLp.title} from the table filter. All tracks and learning periods are listed below.`
            : filterTrackKey && selectedTrack
              ? `Track filter: ${selectedTrack.track_label || selectedTrack.title || 'selected track'}. Expand any learning period to assign forms or review evidence.`
              : 'All learning periods for this enrollment — assign forms and review portfolio status.'
        }
        footer={
          <button
            type="button"
            className="profile-sheet-btn profile-sheet-btn--secondary"
            disabled={portfolioSheetBusy}
            onClick={closePortfolioModal}
          >
            Close
          </button>
        }
      >
        <StudentPortfolioExplorer
            explorer={portfolioModal?.explorer}
            loading={portfolioModal?.loading && !portfolioModal?.explorer}
            highlightLpId={portfolioModal?.highlightLpId}
            highlightTrackKey={portfolioModal?.highlightTrackKey}
            formsCatalog={forms}
            canAssignForms={caps.canAssignForms}
            canCurateEvidence={canCuratePortfolioEvidence}
            canSubmitPortfolios={caps.canSubmitPortfolios || caps.canCurate}
            canReviewPortfolios={caps.canReviewPortfolios}
            portfolioActionBusy={portfolioMutationBusy}
            onPortfolioAction={handlePortfolioAction}
            profileLink={
              portfolioModal?.student
                ? {
                    userId: portfolioModal.student.student_user_id,
                    userGroupsPeriodsId: portfolioModal.student.student_ugp_id,
                    returnPath: profileReturnPath,
                  }
                : null
            }
            assigning={assigning}
            togglingSubmissionId={curatingSubmissionId}
            onToggleSubmission={handleTogglePortfolioSubmission}
            onAssign={(learningPeriodId, formId) =>
              handleAssignForm(portfolioModal?.student, learningPeriodId, formId)
            }
            asOfDate={devAsOfDateApplied || null}
          />
      </ProfileFormSheet>

      <LoadingOverlay
        isVisible={portfolioOverlayVisible}
        message={portfolioOverlayMessage}
        style={{ zIndex: 20050 }}
      />

      <PortfolioEvidenceHelpSheet
        open={!!portfolioModal && showPortfolioHelp}
        onClose={() => setShowPortfolioHelp(false)}
      />

      {cornerNotice ? (
        <CornerNotice {...cornerNotice} onClose={() => setCornerNotice(null)} />
      ) : null}

      <ErrorModal
        isOpen={!!errorModal && !fatalError}
        onClose={() => setErrorModal(null)}
        title={errorModal?.title || 'Error'}
        message={errorModal?.message}
        details={errorModal?.details}
        zIndex={2100}
      />

      {fatalError ? (
        <div
          className="ws-fatal-error-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="ws-fatal-error-title"
          aria-describedby="ws-fatal-error-desc"
        >
          <div className="ws-fatal-error-panel">
            <h2 id="ws-fatal-error-title">{fatalError.title || 'Something went wrong'}</h2>
            <p id="ws-fatal-error-desc">{fatalError.message}</p>
            {fatalError.details ? (
              <pre className="ws-fatal-error-details">{fatalError.details}</pre>
            ) : null}
            <p className="ws-fatal-error-reload">
              Reload this page in your browser to continue. This dialog cannot be dismissed.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default WorkSamplesManager;
